/* ─── Entity Types ──────────────────────────────────────────────── */



export enum EntityType {
  NADLAN   = 'Nadlan',
  CAR      = 'Car',
  SALE2    = 'Sale2',
  CELLULAR = 'CELLULAR',
  FURNITURE= 'FURNITURE',
  FASHION  = 'FASHION',
  BOYBABY  = 'BOYBABY',
  PET      = 'Pet',
  COWORKER = 'Coworker',
  REQUIRED = 'Required',
}

/* ─── Shared sub-models ─────────────────────────────────────────── */
export interface Image {
  image:   string;
  SortNum: number | null;
}

export interface Armature {
  id:              number;
  ArmatureDesc:    string;
  ArmatureIcon:    string;
  MainCategory:    number;
  SocondryCategory:number;
}

export interface Color {
  id:        number;
  colorName: string;
  colorCode: string;
}

export interface NumberOfOwner {
  id:          number;
  numberOnwer: string;   // note: typo preserved from API
  weight?:     number;
}

export interface UserContactDetails {
  id:           number;
  contactPerson:string;
  phoneNumber:  string;
  email:        string;
  acceptTerms:  boolean;
  acceptAdv:    boolean;
  user:         number;
}

export interface MainCategory {
  id:        number;
  name:      string;
  icon:      string;
  category:  string;
  priority:  string;
  important: boolean;
  SortNum:   number;
  mobilePng?: string;
  rootCategory?: string;
  override_fields?: string | null;
  disable_fields?:  string | null;
  disable?: boolean;
}

export interface SecondaryCategory {
  id:           number;
  mainCategoryKey: MainCategory;
  subCategory:  string;
  name:         string;
  icon:         string;
  keyArr:       string;
  priority:     string;
}

/** API returns CodeProducer as a plain string for Car listings */
export interface CodeProducer {
  id:           number;
  producer:     string;
  codeProducer: number;
}

/** API returns MainModel as a plain string for Car listings */
export interface MainModel {
  id:            string;
  model:         string;
  codeMainModel: number;
}

export interface CityModel {
  id:                  number;
  nameHe:              string;
  nameEng:             string;
  nameAr:              string;
  cityCode:            number;
  areaCode:            number;
  areaName:            string;
  manaChamberCode:     number;
  manaChamber:         string;
  regionalCouncilCode: number;
  regionalCouncil:     string;
}

export interface StreetModel {
  id:         number;
  nameHe:     string;
  nameEng:    string;
  nameAr:     string;
  cityCode:   number;
  cityName:   string;
  streetCode: number;
}

export interface OwnerType {
  id:   number;
  Type: string;
}

/* ─── Nadlan-specific ───────────────────────────────────────────── */
export interface RoomsNum   { id: number; numberOfRooms: number; }
export interface ParkingNumber { id: number; numberParking: number; }
export interface StatusOfNadlan { id: number; nameHe: string; nameEng: string; nameAr: string; }
export interface TypeOfNadlan   { id: number; nameHe: string; nameEng: string; nameAr: string; }

/* ─── Sale2-specific (used also for CELLULAR, etc.) ─────────────── */
export interface ProductStatus  { id: number; statusName: string; }
export interface Sale2SubCategory { id: number; subCategoryName: string; sconderyCategory: number; }

/* ─── Coworker-specific ─────────────────────────────────────────── */
export interface CoworkerField {
  id:    number;
  label: string;
  icon:  string;
  value: { value: number; name: string };
}

/* ─── Required-specific ─────────────────────────────────────────── */
export interface RequiredField {
  id:    number;
  label: string;
  icon:  string;
  value: { value: number; name: string };
}

/* ─── Pet-specific ──────────────────────────────────────────────── */
export interface PetField {
  id:    number;
  label: string;
  icon:  string;
  value: { value: number; name: string };
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PRODUCT INTERFACE
   ════════════════════════════════════════════════════════════════════
   All fields are optional (?) except the handful guaranteed on every
   resourcetype, so the template can safely use ?. without runtime errors.
   ═══════════════════════════════════════════════════════════════════ */
export interface Product {
  /* ── Universal ──────────────────────────────────────────────── */
  id:             number | string;
  name:           string;
  price:          number | null;
  discount:       number | null;
  description:    string;
  FreeText?:      string | null;
  images:         Image[];
  armatureList:   Armature[];
  adsRoute?:      any;
  mainCategory:   MainCategory;
  sconderyCategory?: any;
  userContactDetails?: UserContactDetails;
  resourcetype:   EntityType | null;
  statusAds?:     string;
  active?:        boolean;
  createTime?:    string | Date;
  updateTime?:    string | Date;
  video_url?:     string | null;
  video_key?:     string | null;
  average_rating?: string | null;
  total_ratings?:  number;
  polymorphic_ctype?: number;
  impressions_count?: number;

  /* ── Market / e-commerce ────────────────────────────────────── */
  is_market_product?: boolean;
  market_price?:      number | null;
  stock_qty?:         number | null;
  condition?:         string | null;
  shipping_available?: boolean;
  shipping_cost?:     number | null;
  pickup_only?:       boolean;

  /* ── Car ────────────────────────────────────────────────────── */

  CodeProducer?:  string | CodeProducer;

  MainModel?:     string | MainModel;
  year?:          number | null;
  mileage?:       number | null;
  engineCapacity?:number | null;
  gearBox?:       string | number | null;
  licenseNumber?: string | number | null;
  DrivingTest?:   boolean | null;
  YearTest?:      string | null;
  MonthTest?:     string | null;
  monthOnRoad?:   number | null;
  exchange?:      boolean | null;
  DisabledSuitable?: boolean | null;
  date_joined?:   string;
  typeProduct?:   string;
  thirdCategory?: string;
  area?:          string;
  deviceStatus?:  string;
  activeDescription?: string;
  cartCount?:     number;
  availibilityCount?: number;
  shortDescription?:  string;
  sessionId?:     string | null;
  numberOfPays?:  number | null;

  /** lowercase for Car */
  city?:          CityModel | null;
  Color?:         Color | null;
  NumberOfOwner?: NumberOfOwner | null;
  CurrentOwner?:  OwnerType | null;
  PastOwner?:     OwnerType | null;

  /* ── Nadlan ─────────────────────────────────────────────────── */
  /** uppercase for Nadlan */
  City?:          CityModel | null;
  Street?:        StreetModel | null;
  TypeOfNadlan?:  TypeOfNadlan | null;
  StatusOfNadlan?:StatusOfNadlan | null;
  RoomsNum?:      RoomsNum | null;
  ParkingNumber?: ParkingNumber | null;
  Balcony?:       boolean | null;
  Entrance?:      boolean | null;
  HomeNum?:       number | null;
  Floor?:         number | null;
  TotalFloor?:    number | null;
  OnColumns?:     boolean | null;
  Neighborhood?:  string | null;
  AdvsConfirmation?: boolean;
  TermsConfirmation?:boolean;
  Mailers?:       boolean | null;
  properties?:    any[];
  condo?:         number | null;
  arnona?:        number | null;
  buildingMeter?: number | null;
  buildingMeterTotal?: number | null;

  /* ── Sale2 / CELLULAR ───────────────────────────────────────── */
  /** Sale2 uses uppercase City  */
  ProductStatus?: ProductStatus | null;
  subCategory?:   Sale2SubCategory | null;
  /** plain string e.g. "Google" */
  producer?:      string | null;
  /** plain string e.g. "Pixel" */
  model?:         string | null;
  DeliveryOffer?: boolean | null;

  /* ── Coworker ───────────────────────────────────────────────── */
  professionalTitle?:  string;
  professionCategory?: CoworkerField | null;
  serviceType?:        CoworkerField | null;
  availability?:       CoworkerField | null;
  pricingType?:        CoworkerField | null;
  experienceYears?:    CoworkerField | null;
  priceFrom?:          string | number | null;
  priceTo?:            string | number | null;
  freeEstimate?:       boolean;
  businessName?:       string;
  isAnonymous?:        boolean;

  /* ── Required ───────────────────────────────────────────────── */
  jobTitle?:         string;
  jobCategory?:      RequiredField | null;
  jobType?:          RequiredField | null;
  workLocationType?: RequiredField | null;
  region?:           RequiredField | null;
  salaryFrom?:       string | number | null;
  salaryTo?:         string | number | null;
  salaryType?:       RequiredField | null;
  isNegotiable?:     boolean;
  companyName?:      string;

  /* ── Pet ────────────────────────────────────────────────────── */
  petType?:      PetField | null;
  breed?:        PetField | null;
  petName?:      string;
  listingType?:  PetField | null;
  age?:          PetField | null;
  gender?:       PetField | null;
  size?:         PetField | null;
  color?:        string;          // Pet uses plain string
  quantity?:     number;
  freeAdoption?: boolean;
  vaccinated?:   boolean;
  neutered?:     boolean;
  trained?:      boolean;
  pedigree?:     boolean;
  kidFriendly?:  boolean;
  suitableForYard?:      boolean;
  suitableForApartment?: boolean;
  suitability?:  string[];
  sellerType?:   PetField | null;
}


export class TopProduct implements TopProduct {
  AdvsConfirmation: boolean;
  Balcony: boolean | null;
  City: CityModel | null;
  CodeProducer: string | CodeProducer;
  Color: Color | null;
  CurrentOwner: OwnerType | null;
  DeliveryOffer: boolean | null;
  DisabledSuitable: boolean | null;
  DrivingTest: boolean | null;
  Entrance: boolean | null;
  Floor: number | null;
  FreeText: string | null;
  HomeNum: number | null;
  Mailers: boolean | null;
  MainModel: string | MainModel;
  MonthTest: string | null;
  Neighborhood: string | null;
  NumberOfOwner: NumberOfOwner | null;
  OnColumns: boolean | null;
  ParkingNumber: ParkingNumber | null;
  PastOwner: OwnerType | null;
  ProductStatus: ProductStatus | null;
  RoomsNum: RoomsNum | null;
  StatusOfNadlan: StatusOfNadlan | null;
  Street: StreetModel | null;
  TermsConfirmation: boolean;
  TotalFloor: number | null;
  TypeOfNadlan: TypeOfNadlan | null;
  YearTest: string | null;
  active: boolean;
  activeDescription: string;
  adsRoute: any;
  age: PetField | null;
  area: string;
  armatureList: Armature[];
  arnona: number | null;
  availability: CoworkerField | null;
  availibilityCount: number;
  average_rating: string | null;
  breed: PetField | null;
  buildingMeter: number | null;
  buildingMeterTotal: number | null;
  businessName: string;
  cartCount: number;
  city: CityModel | null;
  color: { colorName: string | null; colorCode: string | null } & string;
  companyName: string;
  condo: number | null;
  createTime: string | Date;
  date_joined: string;
  description: string;
  deviceStatus: string;
  discount: number | null;
  engineCapacity: number | null;
  exchange: boolean | null;
  experienceYears: CoworkerField | null;
  freeAdoption: boolean;
  freeEstimate: boolean;
  gearBox: string | number | null;
  gender: PetField | null;
  id: number | string;
  image: string;
  images: Image[];
  isAnonymous: boolean;
  isNegotiable: boolean;
  isNewAds: boolean;
  is_market_product: boolean;
  market_price: number | null;
  stock_qty: number | null;
  condition: string | null;
  shipping_available: boolean;
  shipping_cost: number | null;
  pickup_only: boolean;
  jobCategory: RequiredField | null;
  jobTitle: string;
  jobType: RequiredField | null;
  kidFriendly: boolean;
  licenseNumber: string | number | null;
  link: string;
  listingType: PetField | null;
  mainCategory: MainCategory;
  mileage: number | null;
  model: string | null;
  monthOnRoad: number | null;
  name: string;
  neutered: boolean;
  numberOfPays: number | null;
  pedigree: boolean;
  petName: string;
  petType: PetField | null;
  polymorphic_ctype: number;
  price: number;
  priceFrom: string | number | null;
  priceTo: string | number | null;
  pricingType: CoworkerField | null;
  producer: string | null;
  professionCategory: CoworkerField | null;
  professionalTitle: string;
  properties: any[];
  quantity: number;
  region: RequiredField | null;
  resourcetype: EntityType | null;
  salaryFrom: string | number | null;
  salaryTo: string | number | null;
  salaryType: RequiredField | null;
  sconderyCategory: any;
  sellerType: PetField | null;
  serviceType: CoworkerField | null;
  sessionId: string | null;
  shortDescription: string;
  size: PetField | null;
  statusAds: string;
  subCategory: Sale2SubCategory | null;
  suitability: string[];
  suitableForApartment: boolean;
  suitableForYard: boolean;
  thirdCategory: string;
  total_ratings: number;
  trained: boolean;
  typeProduct: string;
  updateTime: string | Date;
  userContactDetails: UserContactDetails;
  vaccinated: boolean;
  video_key: string | null;
  video_url: string | null;
  workLocationType: RequiredField | null;
  year: number | null;
}
